// File: src/components/pages/static/Help.jsx
// Publicly accessible — no authentication required

import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import '../../../styles/unified.css';

const faqs = [
  {
    q: 'How do I add a new student to BipzApp?',
    a: 'Navigate to the Students page from the sidebar and click "Add Student". Fill in the student\'s details and send them an invitation link to complete their registration.',
  },
  {
    q: 'How do I set up my lesson schedule?',
    a: 'Go to Templates in the sidebar to create your weekly slot templates. You can define your working hours, break times, and lesson durations. Once published, your availability becomes bookable.',
  },
  {
    q: 'How are payments recorded in BipzApp?',
    a: 'Payments are recorded through the Payments page. You can log lesson packages, mark payments as confirmed or pending, and track how many lessons each student has remaining.',
  },
  {
    q: 'Can students log in to BipzApp?',
    a: 'Yes. Once you invite a student via email, they receive a link to complete their registration. Students can then log in to view their lesson progress and booking history.',
  },
  {
    q: 'How do I cancel or reschedule a booking?',
    a: 'Open the Manage Bookings page, find the relevant booking, and use the cancellation or rescheduling options. Students are notified automatically when changes are made.',
  },
  {
    q: 'Is my data secure on BipzApp?',
    /* a: 'Yes. All data is encrypted in transit (TLS/HTTPS) and at rest (AES-256). Authentication is handled by Auth0, a certified identity provider. Your data is stored on AWS infrastructure in the UK (London region).',*/
    a: 'Yes. All data is encrypted in transit. Authentication is handled by Auth0, a certified identity provider. Your data is stored on Cloud infrastructure in the UK (London region).'
  },
];

const FaqItem = ({ q, a }) => {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="static-card"
      style={{ padding: '20px 24px', cursor: 'pointer', userSelect: 'none' }}
      onClick={() => setOpen(!open)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}>
        <span style={{ fontSize: '0.95rem', fontWeight: 600, color: '#1e293b' }}>{q}</span>
        <span style={{
          fontSize: '1.1rem', color: '#7c3aed', flexShrink: 0,
          transition: 'transform 0.2s', transform: open ? 'rotate(45deg)' : 'rotate(0deg)'
        }}>+</span>
      </div>
      {open && (
        <p style={{ marginTop: 12, marginBottom: 0, fontSize: '0.9rem', color: '#475569', lineHeight: 1.75 }}>
          {a}
        </p>
      )}
    </div>
  );
};

const Help = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth0();
  const handleBack = () => navigate(isAuthenticated ? '/dashboard' : '/');

  return (
    <div className="static-page">

      {/* Hero */}
      <div className="static-hero">
        <div className="static-hero-inner">
          <button className="static-back-btn" onClick={handleBack}>
            ← {isAuthenticated ? 'Back to Dashboard' : 'Back to Home'}
          </button>
          <div className="static-hero-icon">🛟</div>
          <div className="static-hero-badge">Help Center</div>
          <h1 className="static-hero-title">How Can We Help?</h1>
          <p className="static-hero-subtitle">
            Browse the frequently asked questions below, or reach out to us directly
            and we will get back to you as soon as we can.
          </p>
        </div>
      </div>

      {/* Content */}
      <div className="static-container">

        {/* FAQs */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
          <h2 style={{ fontSize: '1rem', fontWeight: 700, color: '#1e293b', margin: 0 }}>
            Frequently Asked Questions
          </h2>
          {faqs.map((faq, i) => (
            <FaqItem key={i} q={faq.q} a={faq.a} />
          ))}
        </div>

        {/* Still need help */}
        <div className="static-card">
          <h2 className="static-card-title">
            <span className="static-card-title-icon">📬</span>
            Still Need Help?
          </h2>
          <p>
            For any query or issue not covered above, please get in touch with us directly.
            We are happy to help and will respond as quickly as possible.
          </p>

          <div className="static-contact-row">
            <div className="static-contact-icon">📧</div>
            <div>
              <div className="static-contact-label">Email</div>
              <div className="static-contact-value">
                <a href="mailto:nbipinraj@bipzapp.com">nbipinraj@bipzapp.com</a>
              </div>
            </div>
          </div>

          <div className="static-contact-row">
            <div className="static-contact-icon">📞</div>
            <div>
              <div className="static-contact-label">Phone</div>
              <div className="static-contact-value">
                <a href="tel:+447423393876">+44 7423 39 3876</a>
              </div>
            </div>
          </div>

          <div className="static-contact-row">
            <div className="static-contact-icon">📍</div>
            <div>
              <div className="static-contact-label">Location</div>
              <div className="static-contact-value">Salisbury, England, UK</div>
            </div>
          </div>
        </div>

        <div className="static-highlight">
          <div className="static-highlight-icon">⚡</div>
          <p>
            For faster responses, please include your registered email address and a brief
            description of your query when contacting us. This helps us look up your account
            and resolve your issue without back-and-forth.
          </p>
        </div>

      </div>
    </div>
  );
};

export default Help;
